/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React, { useState } from 'react'
import { Box } from '@mui/material'
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import api from '../../../../api'
import authenticatedFetch from '../../../../api/authenticatedFetch'
import { IndexTable } from '../../../../components/index_table'
import { Loader } from '../../../../components/loader'
import camelize from '../../../../utils/camelize'
import { useNotificationContext } from '../../../../contexts/notificationContext'
import { ConfirmationDialog } from '../../../../components/confirmation_dialog'
import CheckIcon from '@mui/icons-material/Check'
import { useSelector } from 'react-redux'
import { checkAdmin } from '../../../../utils/checkAdmin'
import { buildQueryParams } from '../../../../utils/buildQueryParams'
import {
  StyledIgnoreButton,
} from '../styles'
import { Query_Keys } from '../../../../lib/queryKeys'
import { formatDate } from '../../../../utils/formatDate'
import { useOtMutation } from "../../../../hooks/mutations/useOtMutation"
import { employeeVisibilityMutationKeyGroups } from '../../../../hooks/mutations/queryKeyGroups/employeeVisibilityMutationKeys'
import { resetPageAfterDeletion } from '../../../../utils/resetPageAfterDeletion'
import { formatEmployeeName } from '../../../../utils/employeeLabel'

const IgnoredEmployees = ({
  employeeNameSearch = '',
  onCountChange = () => {},
  onFetchingChange = () => {},
}) => {
  const [page, setPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(25)
  const [sortBy, setSortBy] = useState('')
  const [sortOrder, setSortOrder] = useState('desc')
  const showNotification = useNotificationContext()
  const queryClient = useQueryClient()
  const { admin } = useSelector((state) => state.admin)
  const superAdmin = checkAdmin(admin?.formatted_roles)
  const [loadingUnignore, setLoadingUnignore] = useState(false)
  const [csvLoading, setCsvLoading] = useState(false)
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false)
  const [employeesToUnignore, setEmployeesToUnignore] = useState([])
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState([])

  React.useEffect(() => {
    setSelectedEmployeeIds([])
  }, [page])

  React.useEffect(() => {
    setPage(1)
    setSelectedEmployeeIds([])
  }, [employeeNameSearch])

  const fetchIgnoredEmployees = async () => {
    const res = await api.get('/v1/admin/employees/ignored', {
      params: {
        page: page,
        per_page: itemsPerPage,
        ...(sortBy ? { sort_by: sortBy, sort_order: sortOrder } : {}),
        ...(employeeNameSearch ? { employee_name: employeeNameSearch } : {}),
      }
    })
    return { data: res?.data, pageCount: res?.headers['total-count'] }
  }

  const { data, isFetching } = useQuery({
    queryKey: Query_Keys.employees_ignored({
      page: page,
      per_page: itemsPerPage,
      sortBy,
      sortOrder,
      employeeNameSearch
    }),
    queryFn: fetchIgnoredEmployees,
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  })

  React.useEffect(() => {
    onCountChange(data?.data?.length ?? 0)
  }, [data?.data?.length, onCountChange])

  React.useEffect(() => {
    onFetchingChange(isFetching)
  }, [isFetching, onFetchingChange])

  const { mutateAsync: unignoreEmployeeMutation } = useOtMutation({
    mutationFn: async (payload) => await api.post('/v1/admin/employees/unignore', payload),
    invalidateKeys: [...employeeVisibilityMutationKeyGroups.unignoreEmployee],
    errorMessage: () => null,
    onSuccess: () => {
      showNotification(employeesToUnignore?.length > 1 ? 'Employees unignored successfully' : 'Employee unignored successfully', 'success')
      setConfirmationDialogOpen(false)
      resetPageAfterDeletion({ totalCount: data?.pageCount, removed: employeesToUnignore?.length || 1, page, itemsPerPage, setPage })

      setEmployeesToUnignore([])
      setSelectedEmployeeIds([])
      setLoadingUnignore(false)
    },
    onError: (err) => {
      showNotification(err?.response?.data?.errors?.[0] || 'Failed to unignore employee', 'error')
      setLoadingUnignore(false)
    }
  });

  const handleUnignore = async () => {
    if (!employeesToUnignore?.length) return

    setLoadingUnignore(true)
    try {
      await unignoreEmployeeMutation({
        employee_guids: employeesToUnignore.map((employee) => employee?.employee_guid),
      });
    } catch {
      // Handled in onError
    }
  }

  const handleExportCsv = async () => {
    try {
      setCsvLoading(true)
      const query = buildQueryParams({
        ...(sortBy ? { sort_by: sortBy, sort_order: sortOrder } : {})
      })
      const url = `${process.env.REACT_APP_API_BASE_URL}/v1/admin/csv_reports/export?report=ignored_employees${query ? `&${query}` : ''}`

      const { response } = await authenticatedFetch(url, {
        method: 'GET',
        defaultErrorMessage: 'Failed to download CSV'
      })

      const blob = await response.blob()
      const downloadUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')

      link.href = downloadUrl
      link.download = `ignored_employees_${dayjs(new Date()).format('YYYY-MM-DD')}.csv`
      link.click()

      window.URL.revokeObjectURL(downloadUrl)
      showNotification('CSV downloaded successfully', 'success')
    } catch (error) {
      console.error('CSV download error:', error)
      showNotification(error?.message || 'Failed to download CSV', 'error')
    } finally {
      setCsvLoading(false)
    }
  }

  const TableColumns = [
    { key: 'employee_name', sortable: true },
    { key: 'email', sortable: true },
    { key: 'reason', sortable: true, minWidth: 300 },
    { key: 'restaurant_group', sortable: true },
    { key: 'ignored_at', sortable: true, minWidth: 220 },
    { key: 'ignored_by', sortable: true }
  ]

  const columns = TableColumns.map((c) => ({ key: c.key, label: camelize(c.key), sortable: c.sortable, minWidth: c.minWidth }))

  const rows = (data?.data || []).map((record) => ({
    id: record?.employee_guid,
    employee_name: record?.employee_name,
    email: record?.email,
    reason: record?.reason || '-',
    restaurant_group: record?.restaurant_group?.name,
    ignored_at: formatDate(record?.ignored_at, record?.timezone_abbr),
    ignored_by: record?.ignored_by,
  }))

  const selectedEmployees = (data?.data || []).filter((record) => selectedEmployeeIds.includes(record.employee_guid))

  const handleSortChange = (nextSortBy, nextSortOrder) => {
    setSortBy(nextSortBy)
    setSortOrder(nextSortOrder)
    setPage(1)
  }

  return (
    <React.Fragment>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1, mb: 1 }}>
        <StyledIgnoreButton
          variant="outlined"
          disableRipple
          disabled={superAdmin?.role !== 'admin' || selectedEmployeeIds.length === 0}
          onClick={() => {
            setEmployeesToUnignore(selectedEmployees)
            setConfirmationDialogOpen(true)
          }}
          sx={{ width: 'auto', px: 2.5 }}
        >
          {selectedEmployeeIds?.length > 1 ? `Unignore Employees (${selectedEmployeeIds.length})` : 'Unignore Employee'}
        </StyledIgnoreButton>
      </Box>
      {(isFetching && !data) ?
        <Loader /> :
        <IndexTable
          columns={columns}
          data={rows}
          tableId="employees-without-number-ignored"
          type="entries"
          checkboxWidth="10px"
          sortMode="server"
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSortChange={handleSortChange}
          itemsPerPage={itemsPerPage}
          setItemsPerPage={setItemsPerPage}
          page={page}
          setPage={setPage}
          pageCount={data?.pageCount}
          checkboxes={true}
          selectedIds={selectedEmployeeIds}
          setSelectedIds={setSelectedEmployeeIds}
          report={false}
          showDownloadButton={true}
          disableDownloadButton={data?.data?.length === 0 || csvLoading}
          handleExportCsv={handleExportCsv}
          csvLoading={csvLoading}
          searchLoading={isFetching}
          hasSearchTerm={Boolean(employeeNameSearch)}
        />
      }

      <ConfirmationDialog
        open={confirmationDialogOpen}
        onClose={() => {
          setConfirmationDialogOpen(false)
          setEmployeesToUnignore([])
        }}
        Icon={CheckIcon}
        dialogTitle={employeesToUnignore.length > 1 ? `Are you sure you want to unignore these ${employeesToUnignore.length} employees?` : 'Are you sure you want to unignore this employee?'}
        dialogBody={employeesToUnignore.length > 1
          ? `This action will unignore ${employeesToUnignore.length} selected employees from this report.`
          : `This action will unignore ${formatEmployeeName(employeesToUnignore[0])} from this report.`}
        confirmationText={employeesToUnignore.length > 1 ? 'Yes, I want to unignore these employees' : 'Yes, I want to unignore this employee'}
        handleConfirm={handleUnignore}
        isLoading={loadingUnignore}
      />
    </React.Fragment>
  )
}

export default IgnoredEmployees
